import { processMatch } from "@/features/match/processMatch";
import { resolveBetsInPlace, updatePersonalBests } from "@/features/player/playerHelpers";
import type { Slice } from "@/store/createSlice";
import type { RecordMatchIntent } from "@/store/types";
import type { Match, MatchResult } from "@/types";
import type { MatchId, MatchResultId } from "@/types/branded";
import { toMatchId } from "@/types/branded";
import { generateUid } from "@/utils/index";

// ── Slice ──────────────────────────────────────────────────────

export const createMatchSlice: Slice<{
  matches: Record<MatchId, Match>;
  results: Record<MatchResultId, MatchResult>;
  recordMatch: (intent: RecordMatchIntent) => void;
  deleteMatch: (matchId: MatchId, resultId: MatchResultId) => void;
}> = (set, _get) => ({
  matches: {},
  results: {},

  recordMatch: (intent) =>
    set(
      (s) => {
        const { activityId, teams, ranks } = intent;
        const activity = s.activities[activityId];
        if (!activity) return;

        const match: Match = {
          id: toMatchId(generateUid()),
          activityId,
          teams,
          ranks,
          createdAt: Date.now(),
        };

        const { updatedPlayers, result } = processMatch({
          match,
          players: s.players,
          activity,
          multiplierConfig: s.multiplierConfig,
          context: { recentMatches: Object.values(s.matches), playersById: s.players },
        });

        // Write players, update personal bests — all on the draft
        for (const p of updatedPlayers) {
          updatePersonalBests(p);
          s.players[p.id] = p;
        }

        s.matches[match.id] = match;
        s.results[result.id] = result;

        // Status event
        const winningTeams = result.teamResults.filter((t) => t.rank === 1);
        if (winningTeams.length > 0) {
          const winnerNames = winningTeams
            .map((t) => t.players.map((p) => p.name).join(" & "))
            .join(" & ");
          const points = winningTeams
            .map((t) => t.players.map((p) => `+${Math.round(p.matchPoints)}`).join(", "))
            .join(", ");

          const event = {
            text: `${winnerNames} won ${activity.name} (${points})`,
            timestamp: Date.now(),
          };
          s.lastStatusEvent = event;
          s.statusHistory.push(event);
        }

        // Resolve bets
        const winningTeamIdx = result.teamResults.find((t) => t.rank === 1)?.teamIndex ?? -1;
        const winnerPlayerIds = winningTeamIdx >= 0 ? (match.teams[winningTeamIdx] ?? []) : [];

        if (winnerPlayerIds.length > 0) {
          resolveBetsInPlace(s.bets, s.players, activityId, winnerPlayerIds);
        }
      },
      false,
      "match/record",
    ),

  deleteMatch: (matchId, resultId) =>
    set(
      (s) => {
        delete s.matches[matchId];
        delete s.results[resultId];
      },
      false,
      "match/delete",
    ),
});
